import { FileServiceClient } from '@shared/files/FileServiceClient';
import { LStorage } from '@shared/Storage';
import { Button } from '@shared/ui-react/Button';
import { Notify } from '@shared/ui-native/Notify';
import { DateUtils } from '@shared/utils/DateUtils';
import React, { useEffect, useState, useRef } from 'react';
import './Pixelpacker.scss';
import { PixelPackerService, ExportJob, ExportStatus } from './PixelPackerService';
import { DialogUtils } from '@shared/ui-react/Dialog';

export const PixelPackerWindow = {
    open: (imageUuids?: string[], autoStart = false) => {
        if (!kuura.hasPrivilege('Download_Image')) {
            Notify.warn(__('general.validation.access_not_allowed'));
            return;
        }

        DialogUtils.renderComponent({
            title: kuura.getMessage('js_images.download_annotated_images') + ' (BETA)',
            body: <PixelPackerWindowComponent imageUuids={imageUuids} autoStart={autoStart} />,
        });
    },
};

const MESSAGES = {
    EXPORT_ALL: kuura.getMessage('general.export'),
    EXPORT_SELECTED: kuura.getMessage('general.export_selected'),
    INIT: kuura.getMessage('js_files.initializing'),
    PREPARING: kuura.getMessage('js_files.processing'),
    GATHERING: kuura.getMessage('js_files.gathering'),
    COMPRESSING: kuura.getMessage('js_models.please_wait'),
    COMPLETE: kuura.getMessage('js_models.complete'),
    FAILED: kuura.getMessage('js_models.failed'),
    DOWNLOAD: kuura.getMessage('js_files.download_btn'),
    ARCHIVE_CREATED: kuura.getMessage('js_images.archive_created'),
};

function PixelPackerWindowComponent({ imageUuids, autoStart }: { imageUuids?: string[]; autoStart: boolean }) {
    const [exportInProgress, setExportInProgress] = useState(false);
    const [exportFinished, setExportFinished] = useState(false);

    const exportJob = useRef<ExportJob | null>(null);

    // useRef() is used here instead of useState() to avoid React state update timing issues. This
    // properly renders the timestamp immediately after the export has been completed.
    const timestamp = useRef<string>('');
    const timeoutId = useRef<ReturnType<typeof setInterval>>(null);
    const downloadLink = useRef<string>('');

    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [annotatedImages, setAnnotatedImages] = useState(null);
    const [totalImages, setTotalImages] = useState(null);

    useEffect(() => {
        const initLatestStatus = async () => {
            const status = await PixelPackerService.getLatestStatusForProject(kuura.getProjectUuid());
            if (status) {
                setAnnotatedImages(status.annotatedImages);
                setTotalImages(status.totalImages);
                setMessage(status.stage ? MESSAGES[status.stage] : '');

                if (status.stage === 'COMPLETE') {
                    timestamp.current = DateUtils.formatDateTime(new Date(status.finishedAt * 1000).getTime());
                    downloadLink.current = await FileServiceClient.getDownloadUrl(status.resultObjectKey);
                    setExportFinished(true);
                } else if (status.stage === 'FAILED') {
                    setErrorMessage(__('general.export.error.failed_export'));
                } else {
                    // If the latest export job is still ongoing, we need to poll for its status.
                    setExportInProgress(true);
                    exportJob.current = {
                        jobUuid: status.jobUuid,
                        startedAt: status.startedAt,
                    };
                }
            }
        };

        if (autoStart && imageUuids) {
            startExport(imageUuids);
        }

        initLatestStatus();
        pollStatus();
        timeoutId.current = setInterval(pollStatus, 5000);

        return () => {
            clearInterval(timeoutId.current);
        };
    }, []);

    const startExport = async (imageUuids?: string[]): Promise<void> => {
        if (exportInProgress && !exportFinished) return;

        if (imageUuids && imageUuids.length === 0) {
            Notify.error(__('general.export_selected.error.no_selected_images'));
            return;
        }

        setExportInProgress(true);
        setExportFinished(false);
        setMessage(MESSAGES.INIT);
        setErrorMessage('');
        setAnnotatedImages(null);
        setTotalImages(null);
        timestamp.current = '';
        downloadLink.current = '';

        try {
            exportJob.current = await PixelPackerService.startExport(imageUuids);
        } catch (error) {
            setExportInProgress(false);
            console.error(error);
            setErrorMessage(__('general.export.error.failed_export_start', error.message ?? error.errorMsg));
            return;
        }

        clearInterval(timeoutId.current);
        timeoutId.current = setInterval(pollStatus, 5000);
    };

    const pollStatus = async (): Promise<void> => {
        if (!exportJob.current) {
            return;
        }

        setMessage(MESSAGES.INIT);
        const status: ExportStatus | undefined = await PixelPackerService.getStatusForExportJob(exportJob.current);
        const startTime = new Date(exportJob.current.startedAt * 1000);

        // Jobs that have been ongoing for more than 3 hours should be dismissed on the frontend, allowing the end user
        // to start a new export. This prevents the frontend from getting stuck in a state where it searches for a job that
        // does not exist.
        if ((!status || status.stage !== 'COMPLETE') && new Date().getTime() - startTime.getTime() > 3600 * 1000 * 3) {
            setExportInProgress(false);
            setExportFinished(false);
            LStorage.removeItem(kuura.getProjectUuid() + '_ANNOTATION');
            clearInterval(timeoutId.current);
            return;
        }

        // If a status for a job was not found but the job is not older than 3 hours, it likely means that the job has
        // not been started in AWS Lambda yet -> return nothing and keep polling.
        if (!status) {
            return;
        }

        if (status.stage === 'FAILED') {
            setExportInProgress(false);
            setExportFinished(false);
            setErrorMessage(__('general.export.error.failed_export'));
            // Do not remove failed exportJob from localStorage, since it can be useful to see that
            // a previous exportJob has failed.
            clearInterval(timeoutId.current);
            return;
        }

        setAnnotatedImages(status.annotatedImages);
        setTotalImages(status.totalImages);
        setMessage(MESSAGES[status.stage]);

        if (status.stage === 'COMPLETE') {
            if (!timestamp.current) {
                timestamp.current = DateUtils.formatDateTime(new Date(status.finishedAt * 1000).getTime());
            }

            if (!downloadLink.current) {
                const downloadUrl = await FileServiceClient.getDownloadUrl(status.resultObjectKey);
                downloadLink.current = downloadUrl;
            }

            setExportInProgress(false);
            setExportFinished(true);
            clearInterval(timeoutId.current);
        }
    };

    const download = () => {
        const link = document.createElement('a');
        link.href = downloadLink.current;
        link.click();
    };

    return (
        <div id='pixelpacker-window'>
            {!exportInProgress && (
                <div id='export-buttons'>
                    <Button className='pixelpacker-button' disabled={exportInProgress} icon='iki iki-external' title='Start export' onClick={() => startExport()}>
                        {MESSAGES.EXPORT_ALL}
                    </Button>
                    <Button
                        className='pixelpacker-button'
                        disabled={exportInProgress}
                        icon='iki iki-external'
                        title='Start selected export'
                        onClick={() => startExport(imageUuids)}
                    >
                        {MESSAGES.EXPORT_SELECTED}
                    </Button>
                </div>
            )}
            {errorMessage && <div id='error-boundary'>{errorMessage}</div>}
            {exportInProgress && !exportFinished && (
                <div id='export-status-box'>
                    {message && message}
                    <span id='export-loader'></span>
                    {annotatedImages !== null && totalImages !== null && (
                        <span>
                            {annotatedImages} / {totalImages}
                        </span>
                    )}
                </div>
            )}
            {exportFinished && (
                <div id='download-box'>
                    <Button className='pixelpacker-button' icon='iki iki-download' onClick={() => download()}>
                        {MESSAGES.DOWNLOAD}
                    </Button>
                    <span>
                        {MESSAGES.ARCHIVE_CREATED}: {timestamp.current}
                    </span>
                    <span>{__('general.export.annotation_result', annotatedImages, totalImages)}</span>
                </div>
            )}
        </div>
    );
}
